import { Button } from '@thanx/uikit/button'
import { TooltipWhenDisabled } from 'components/TooltipWhenDisabled'
import { buildTranslate } from 'locales'
import React from 'react'
import { TOUR_ANCHORS } from 'scenes/RecoveryAI/Sandbox/tourAnchors'
import { useStyletron } from 'styletron-react'

const t = buildTranslate('recovery_ai.wizard')

// AI-865 / AI-882 / AI-1174: Continue/Finalize deliberately shares
// kind="secondary_outline" with Cancel/Back so all three stay pixel-identical
// (transparent fill, 2px solid white border, 4px radius) against the teal
// header bar. Do not reintroduce a custom `overrides` here (ENG-963 tried a
// hand-rolled solid-teal button, which is how this regressed) — baseui's
// BaseButton sets its border as per-side longhands, which defeat a shorthand
// override at equal Styletron specificity (uikit/button/style.js:23-25,
// https://github.com/uber-web/baseui/issues/1779).

type Props = {
  onCancel: () => void
  onContinue: () => void
  onBack?: () => void
  // Override the Cancel button label. AI-892: when the step was entered from the
  // activation summary (?returnTo=activation), Cancel returns there, so it reads
  // "Back to activation" rather than the discard-implying "Cancel".
  cancelLabel?: string
  continueDisabled?: boolean
  // Hide the generic Continue button when a stage renders its own primary action.
  hideContinue?: boolean
  // Override the Continue button label (e.g. "Finalize" on the final stage).
  continueLabel?: string
  // id of an element describing WHY Continue is disabled (e.g. the activation
  // cue), wired to the button as aria-describedby so a screen-reader user hears
  // the blocking reason on focus (WCAG 4.1.3). Omit when not blocked.
  continueAriaDescribedby?: string
  // Hover text naming why Continue is disabled (e.g. the Locations stage's
  // empty-subset / unsaved-edit gates). Wrapped in TooltipWhenDisabled so it's
  // also reachable by keyboard, not just mouse hover. Omit for reasons that
  // don't have dedicated tooltip copy — falls back to the plain button.
  continueTooltip?: string
  isSubmitting?: boolean
}

const WizardNavButtons = ({
  onCancel,
  onContinue,
  onBack,
  cancelLabel,
  continueDisabled = false,
  hideContinue = false,
  continueLabel,
  continueAriaDescribedby,
  continueTooltip,
  isSubmitting = false,
}: Props) => {
  const [css] = useStyletron()

  return (
    <div
      className={css({ display: 'flex', gap: '16px', alignItems: 'center' })}
    >
      <Button kind="secondary_outline" onClick={onCancel}>
        {cancelLabel ?? t('cancel')}
      </Button>
      {onBack && (
        <Button
          kind="secondary_outline"
          onClick={onBack}
          startEnhancer={() => (
            <i className="fa fa-arrow-left" aria-hidden="true" />
          )}
        >
          {t('back')}
        </Button>
      )}
      {!hideContinue && (
        // TooltipWhenDisabled only wraps when continueTooltip is set (e.g. the
        // Locations stage's empty-subset / unsaved-edit gates) — every other
        // disabled reason falls through to the plain button unchanged.
        <TooltipWhenDisabled
          disabled={Boolean(continueTooltip)}
          tooltip={continueTooltip ?? ''}
        >
          {/* Span carries the coach-mark anchor (the AI-162 second tour points
              its final step at Continue): a uikit Button doesn't reliably
              forward data-* to the DOM node the spotlight measures, so anchor
              the wrapper. */}
          <span
            data-tour={TOUR_ANCHORS.continueButton}
            className={css({ display: 'inline-flex' })}
          >
            <Button
              kind="secondary_outline"
              onClick={onContinue}
              disabled={continueDisabled || isSubmitting}
              aria-describedby={continueAriaDescribedby}
              isLoading={isSubmitting}
              endEnhancer={() => (
                <i className="fa fa-arrow-right" aria-hidden="true" />
              )}
            >
              {continueLabel ?? t('continue')}
            </Button>
          </span>
        </TooltipWhenDisabled>
      )}
    </div>
  )
}

export default WizardNavButtons
